import { Box, Flex, Grid, HStack, Icon, Tag, Text } from '@chakra-ui/react';
import {
  NotificationType,
  TransactionNotificationLogFragment,
  TransactionStageType,
} from '@client/graphql/__generated__/types';
import dayjs from 'dayjs';
import {
  FileText,
  Hourglass,
  LucideIcon,
  Mail,
  MessageSquare,
  Send,
  User,
} from 'lucide-react';
import { FC } from 'react';
import { contactTypeLabels } from './contactTypeLabels';

interface NotificationRowProps {
  notification: TransactionNotificationLogFragment;
  onViewDetails: () => void;
}

const eventTypeConfig: Record<
  TransactionStageType,
  { label: string; icon: LucideIcon }
> = {
  [TransactionStageType.LEAD]: { label: 'Lead', icon: User },
  [TransactionStageType.SIGNED_AGENCY_AGREEMENT]: {
    label: 'Agency Agreement',
    icon: User,
  },
  [TransactionStageType.ACTIVE]: { label: 'Active', icon: FileText },
  [TransactionStageType.SUBMITTED_OFFER]: {
    label: 'Submitted Offer',
    icon: Send,
  },
  [TransactionStageType.UNDER_CONTRACT]: {
    label: 'Offer Pending',
    icon: Hourglass,
  },
  [TransactionStageType.CLOSED]: { label: 'Closing', icon: FileText },
  [TransactionStageType.TERMINATED]: {
    label: 'Terminated',
    icon: FileText,
  },
};

const formatDate = (date: string | Date): string => {
  return dayjs(date).format('ddd MMM DD h:mmA');
};

export const NotificationRow: FC<NotificationRowProps> = ({
  notification,
  onViewDetails,
}) => {
  const template = notification.notificationTemplate;
  const eventType = template?.eventType ?? TransactionStageType.LEAD;
  const notificationType = template?.notificationType ?? NotificationType.EMAIL;
  const eventConfig = eventTypeConfig[eventType] ?? {
    icon: FileText,
    label: template?.eventType ?? eventType,
  };

  return (
    <Grid
      _hover={{ bg: 'whiteAlpha.50' }}
      borderBottom="1px solid"
      borderColor="whiteAlpha.100"
      gap={5}
      gridTemplateColumns="1fr 1fr 1fr 1fr 1fr 160px"
      px={5}
      py={3}
      transition="background 0.15s"
    >
      {/* Title */}
      <Text color="white" fontSize="sm" fontWeight="medium">
        {notification.title || template?.title || 'Untitled'}
      </Text>

      {/* Recipients */}
      <Flex direction="column" gap={2}>
        {notification.recipients.map((recipient) => (
          <HStack key={recipient.id} spacing={2}>
            <Text color="white" fontSize="sm" fontWeight="medium">
              {recipient.firstName} {recipient.lastName}
            </Text>
            <Tag
              bg="whiteAlpha.100"
              color="whiteAlpha.600"
              fontSize="xs"
              size="sm"
            >
              {contactTypeLabels[recipient.contactType] ||
                recipient.contactType}
            </Tag>
          </HStack>
        ))}
      </Flex>

      {/* Event */}
      <HStack spacing={2}>
        <Flex
          alignItems="center"
          bgGradient="linear(to-br, purple.400, purple.600)"
          borderRadius="md"
          h={8}
          justifyContent="center"
          w={8}
        >
          <Icon as={eventConfig.icon} boxSize={4} color="white" />
        </Flex>
        <Text color="white" fontSize="sm">
          {eventConfig.label}
        </Text>
      </HStack>

      {/* Type */}
      <HStack spacing={1}>
        <Icon
          as={
            notificationType === NotificationType.EMAIL ? Mail : MessageSquare
          }
          boxSize={3.5}
          color="white"
        />
        <Text color="white" fontSize="sm">
          {notificationType === NotificationType.EMAIL
            ? 'Email'
            : 'Text Message'}
        </Text>
      </HStack>

      {/* Message */}
      <Box
        bg="whiteAlpha.50"
        borderBottomRightRadius="xl"
        borderTopLeftRadius="xl"
        borderTopRightRadius="xl"
        p={2.5}
      >
        <Text
          color="whiteAlpha.700"
          fontSize="sm"
          noOfLines={2}
          overflow="hidden"
        >
          {notification.messageContent}
        </Text>
        <Text
          _hover={{ textDecoration: 'underline' }}
          color="cyan.400"
          cursor="pointer"
          fontSize="sm"
          fontWeight="medium"
          mt={1}
          role="button"
          tabIndex={0}
          onClick={onViewDetails}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onViewDetails();
            }
          }}
        >
          more
        </Text>
      </Box>

      {/* Date */}
      <Text color="whiteAlpha.600" fontSize="sm">
        {notification.sentAt
          ? formatDate(notification.sentAt as string)
          : notification.scheduledAt
            ? formatDate(notification.scheduledAt as string)
            : formatDate(notification.createdAt as string)}
      </Text>
    </Grid>
  );
};
